/**
 * Chat-message file metadata normalization.
 *
 * Chat messages carry file attachments in one of two metadata shapes:
 *
 * New (understood today):
 * ```jsonc
 * "metadata": { "filesIdList": ["<fileUuid1>", "<fileUuid2>"] }
 * ```
 *
 * Legacy (Zendesk-bridged):
 * ```jsonc
 * "metadata": {
 *   "files": "key1,key2,key3",          // CSV of internal KEYS, not file ids
 *   "file.key1.source": "<fileUuid>",   // <-- the real downloadable file id
 *   "file.key1.name": "report.pdf",
 *   "file.key1.size": "220495",
 *   "file.key1.type": "application/pdf",
 *   "message:original-source": "zendesk"
 * }
 * ```
 *
 * ⚠️ Critical: in the legacy shape the CSV entries are *keys*, not file ids. The
 * real, downloadable file id lives in `file.<key>.source`. Using the key directly
 * makes files *appear* but every download/preview 404s. We mirror the backend's
 * own resolution (`MessageDto.extractFileIds()`,
 * `backend-gwt/.../messaging/service/dto/MessageDto.java:142-160`): each key →
 * `file.<key>.source`, dropping keys with no source.
 */
import type { FileRef } from "./fileUtils";

const asString = (v: unknown): string | null =>
  typeof v === "string" && v.length > 0 ? v : null;

/**
 * Normalizes chat-message file metadata into FileRef[], supporting both:
 *  - new shape:    metadata.filesIdList: string[]
 *  - legacy shape: metadata.files = "key1,key2"  +  file.<key>.source|name|type|size
 *
 * For the legacy shape the downloadable id is `file.<key>.source` (NOT the key itself);
 * entries without a source are dropped. Mirrors backend MessageDto.extractFileIds().
 */
export function extractFileRefsFromMetadata(
  metadata: Record<string, unknown> | undefined
): FileRef[] {
  if (!metadata) {
    return [];
  }

  // 1. New shape — already a list of file ids.
  const list = metadata.filesIdList;
  if (Array.isArray(list)) {
    const refs = list
      .filter((id): id is string => typeof id === "string" && id.length > 0)
      .map((fileId) => ({ fileId, name: null, type: null, size: null }));
    if (refs.length > 0) {
      return refs;
    }
  }

  // 2. Legacy shape — CSV of keys; resolve each to file.<key>.source.
  const csv = metadata.files;
  if (typeof csv === "string" && csv.trim().length > 0) {
    return csv
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0)
      .map((key) => {
        const fileId = asString(metadata[`file.${key}.source`]);
        if (!fileId) {
          return null;
        } // key alone is not downloadable — drop it
        const sizeStr = asString(metadata[`file.${key}.size`]);
        const size = sizeStr != null ? Number.parseInt(sizeStr, 10) : null;
        return {
          fileId,
          name: asString(metadata[`file.${key}.name`]),
          type: asString(metadata[`file.${key}.type`]),
          size: Number.isFinite(size) ? size : null
        } satisfies FileRef;
      })
      .filter((ref): ref is FileRef => ref !== null);
  }

  return [];
}

/** Convenience for callers that only need ids (PMP). */
export function extractFileIdsFromMetadata(
  metadata: Record<string, unknown> | undefined
): string[] {
  return extractFileRefsFromMetadata(metadata).map((f) => f.fileId);
}
